import { useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import {
    ArrowLeft,
    Info,
    Activity,
    AlertTriangle,
    BarChart3,
    Layers,
    Shield,
    TrendingUp,
    RefreshCw,
} from "lucide-react";
import { FaGitlab } from "react-icons/fa6";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { Button } from "../../../ui/common-ui/button";
import { Badge } from "../../../ui/common-ui/badge";
import { NGROK } from "../../../../APIs";
import GitlabRecentActivity from "./GitlabRecentActivity";

type EndpointType = "user" | "project" | "group" | "membership";

interface GitlabEntityAnalyticsProps {
    tenantName: string;
    instanceUrl: string;
    endpointType: EndpointType;
    entityValue: string;
    displayName: string;
    entityData: any;
    onBack: () => void;
}

interface AnalyticsResponse {
    tenantName: string;
    instanceUrl: string;
    entity: string;
    entityAssociated: string;
    actionCount: number;
    mergedFrom: string | null;
    mergedTo: string | null;
    actionUsage: Record<string, number>;
    resourceUsage: Record<string, number>;
    categoryUsage: Record<string, number>;
    riskUsage: Record<string, number>;
}

const RISK_ORDER = ["critical", "high", "medium", "low", "info"];
const RISK_COLORS: Record<string, string> = {
    critical: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-yellow-500",
    low: "bg-blue-500",
    info: "bg-gray-400",
};
const RISK_TEXT_COLORS: Record<string, string> = {
    critical: "text-red-600 dark:text-red-400",
    high: "text-orange-600 dark:text-orange-400",
    medium: "text-yellow-600 dark:text-yellow-400",
    low: "text-blue-600 dark:text-blue-400",
    info: "text-gray-600 dark:text-gray-400",
};

const DONUT_COLORS = [
    "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899",
    "#F97316", "#06B6D4", "#84CC16", "#EAB308", "#A855F7",
];

function SkeletonBlock({ className = "" }: { className?: string }) {
    return <div className={`animate-pulse rounded bg-muted ${className}`} />;
}

export default function GitlabEntityAnalytics({
    tenantName,
    instanceUrl,
    endpointType,
    entityValue,
    displayName,
    entityData,
    onBack,
}: GitlabEntityAnalyticsProps) {
    const [activeTab, setActiveTab] = useState<"details" | "activity">("details");

    const {
        data: analytics,
        isLoading,
        isError,
        refetch,
    } = useQuery<AnalyticsResponse>({
        queryKey: ["gitlab-analytics", endpointType, tenantName, instanceUrl, entityValue],
        queryFn: async () => {
            const { data } = await axios.get(
                `${NGROK}/api/saas/gitlab/data/entity`,
                {
                    params: {
                        tenantName,
                        instanceUrl,
                        entity: entityValue,
                    },
                }
            );
            return data;
        },
        enabled: !!tenantName && !!instanceUrl && !!entityValue,
    });

    const entityLabel = analytics?.entityAssociated || displayName || analytics?.entity || entityValue;

    const topActions = Object.entries(analytics?.actionUsage || {})
        .sort(([, a], [, b]) => Number(b) - Number(a))
        .slice(0, 10) as [string, number][];

    const topResources = Object.entries(analytics?.resourceUsage || {})
        .sort(([, a], [, b]) => Number(b) - Number(a))
        .slice(0, 10) as [string, number][];

    const totalResourceUsage = topResources.reduce((sum, [, count]) => sum + Number(count), 0);

    const categoryEntries = Object.entries(analytics?.categoryUsage || {})
        .sort(([, a], [, b]) => Number(b) - Number(a)) as [string, number][];

    const totalCategoryCount = categoryEntries.reduce((sum, [, count]) => sum + Number(count), 0);

    const riskEntries = RISK_ORDER
        .filter((r) => (analytics?.riskUsage || {})[r] !== undefined)
        .map((r) => [r, Number((analytics?.riskUsage || {})[r])] as [string, number]);

    const topCategory = categoryEntries.length > 0 ? categoryEntries[0][0] : "-";
    const highestRisk = riskEntries.find(([, count]) => count > 0)?.[0] || "-";

    const mergedRange =
        analytics?.mergedFrom && analytics?.mergedTo
            ? `${new Date(analytics.mergedFrom).toLocaleDateString()} – ${new Date(analytics.mergedTo).toLocaleDateString()}`
            : "Last 30 days";

    return (
        <div className="flex flex-col gap-6 p-6">
            {/* Entity Info Card */}
            <Card className="portal-card-elevated">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <Button
                            variant="ghost"
                            onClick={onBack}
                            className="gap-2 -ml-2"
                            data-testid="button-back"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </Button>
                        <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                                Instance: {instanceUrl || "-"}
                            </p>
                        </div>
                    </div>
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 flex items-center justify-center border border-orange-200 dark:border-orange-700">
                                <FaGitlab className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="flex-1">
                                <h1 className="text-lg font-semibold mb-0.5">{entityLabel}</h1>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="secondary" className="text-xs capitalize">{endpointType}</Badge>
                                    <span className="text-xs text-muted-foreground">Entity: {entityValue}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <div className="flex justify-center gap-2">
                <button
                    onClick={() => setActiveTab("details")}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors ${activeTab === "details"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 text-muted-foreground hover-elevate"
                        }`}
                    data-testid="tab-details"
                >
                    <Info className="w-4 h-4" />
                    Details
                </button>
                <button
                    onClick={() => setActiveTab("activity")}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors ${activeTab === "activity"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 text-muted-foreground hover-elevate"
                        }`}
                    data-testid="tab-activity"
                >
                    <Activity className="w-4 h-4" />
                    Recent Activity
                </button>
            </div>

            {/* Error State */}
            {isError && (
                <Card className="border-destructive/50">
                    <CardContent className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-destructive" />
                            <span className="text-sm text-destructive">Failed to load analytics data.</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Retry
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* DETAILS TAB */}
            {activeTab === "details" && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    {isLoading ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Card key={i} className="portal-card-elevated">
                                    <CardContent className="p-4 space-y-2">
                                        <SkeletonBlock className="h-3 w-20" />
                                        <SkeletonBlock className="h-6 w-16" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card className="portal-card-elevated">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <BarChart3 className="w-4 h-4 text-primary" />
                                        <span className="text-xs text-muted-foreground font-medium">Total Actions</span>
                                    </div>
                                    <div className="text-2xl font-bold">{(analytics?.actionCount || 0).toLocaleString()}</div>
                                </CardContent>
                            </Card>
                            <Card className="portal-card-elevated">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <TrendingUp className="w-4 h-4 text-primary" />
                                        <span className="text-xs text-muted-foreground font-medium">Merged Range</span>
                                    </div>
                                    <div className="text-sm font-semibold">{mergedRange}</div>
                                </CardContent>
                            </Card>
                            <Card className="portal-card-elevated">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Layers className="w-4 h-4 text-primary" />
                                        <span className="text-xs text-muted-foreground font-medium">Top Category</span>
                                    </div>
                                    <div className="text-sm font-semibold">{topCategory}</div>
                                </CardContent>
                            </Card>
                            <Card className="portal-card-elevated">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        <span className="text-xs text-muted-foreground font-medium">Highest Risk</span>
                                    </div>
                                    <div className={`text-sm font-semibold capitalize ${RISK_TEXT_COLORS[highestRisk] || ""}`}>
                                        {highestRisk}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Top Resources + Top Actions */}
                    {isLoading ? (
                        <div className="grid grid-cols-2 gap-6">
                            <Card className="portal-card-elevated"><CardContent className="p-6"><SkeletonBlock className="h-56 w-full" /></CardContent></Card>
                            <Card className="portal-card-elevated"><CardContent className="p-6"><SkeletonBlock className="h-56 w-full" /></CardContent></Card>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-6">
                            {/* Top Resources - Donut Chart */}
                            <Card className="portal-card-elevated">
                                <CardHeader className="border-b pb-4">
                                    <CardTitle className="text-base font-medium">Top Resources</CardTitle>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Most accessed resources by {endpointType}
                                    </p>
                                </CardHeader>
                                <CardContent className="py-8">
                                    {topResources.length === 0 ? (
                                        <div className="text-center py-8 text-sm text-muted-foreground">No analytics yet</div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-6">
                                            <div className="relative w-56 h-56">
                                                <svg viewBox="0 0 200 200" className="transform -rotate-90">
                                                    {topResources.map(([name, count], idx) => {
                                                        const percentage = (count / totalResourceUsage) * 100;
                                                        const prevPercentages = topResources
                                                            .slice(0, idx)
                                                            .reduce((sum, [, c]) => sum + (c / totalResourceUsage) * 100, 0);
                                                        const circumference = 2 * Math.PI * 70;
                                                        const offset = circumference * (1 - percentage / 100);
                                                        const rotation = (prevPercentages / 100) * 360;

                                                        return (
                                                            <circle
                                                                key={name}
                                                                cx="100"
                                                                cy="100"
                                                                r="70"
                                                                fill="none"
                                                                stroke={DONUT_COLORS[idx % DONUT_COLORS.length]}
                                                                strokeWidth="32"
                                                                strokeDasharray={circumference}
                                                                strokeDashoffset={offset}
                                                                style={{
                                                                    transformOrigin: "100px 100px",
                                                                    transform: `rotate(${rotation}deg)`,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold">{totalResourceUsage.toLocaleString()}</div>
                                                        <div className="text-xs text-muted-foreground mt-1">Total Access</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="w-full space-y-2.5">
                                                {topResources.map(([name, count], idx) => {
                                                    const percentage = (count / totalResourceUsage) * 100;
                                                    return (
                                                        <div key={name} className="flex items-center gap-3 cursor-default" title={`${count.toLocaleString()} accesses`}>
                                                            <div
                                                                className="w-3 h-3 rounded-full flex-shrink-0"
                                                                style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm truncate font-medium">{name}</div>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <span className="text-sm font-semibold">{percentage.toFixed(1)}%</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Top Actions - Bar Chart */}
                            <Card className="portal-card-elevated">
                                <CardHeader className="border-b pb-4">
                                    <CardTitle className="text-base font-medium">Top Actions</CardTitle>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Most frequently performed actions
                                    </p>
                                </CardHeader>
                                <CardContent className="py-6">
                                    {topActions.length === 0 ? (
                                        <div className="text-center py-8 text-sm text-muted-foreground">No analytics yet</div>
                                    ) : (
                                        <div className="space-y-3">
                                            {topActions.map(([action, count]) => {
                                                const maxCount = topActions[0][1];
                                                const percentage = (count / maxCount) * 100;
                                                const totalPercentage = analytics?.actionCount
                                                    ? (count / analytics.actionCount) * 100
                                                    : 0;

                                                return (
                                                    <div key={action} className="space-y-1.5 cursor-default" title={`${count.toLocaleString()} occurrences`}>
                                                        <div className="flex items-baseline justify-between gap-3">
                                                            <div className="text-sm font-medium font-mono truncate flex-1">{action}</div>
                                                            <div className="flex-shrink-0">
                                                                <span className="text-sm font-semibold">{totalPercentage.toFixed(1)}%</span>
                                                            </div>
                                                        </div>
                                                        <div className="bg-muted rounded-sm h-2 overflow-hidden">
                                                            <div
                                                                className="bg-primary h-full rounded-sm transition-all"
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Category Breakdown + Risk Breakdown */}
                    {!isLoading && (
                        <div className="grid grid-cols-2 gap-6">
                            {/* Category Breakdown */}
                            <Card className="portal-card-elevated">
                                <CardHeader className="border-b pb-4">
                                    <CardTitle className="text-base font-medium">Category Breakdown</CardTitle>
                                    <p className="text-xs text-muted-foreground mt-1">Security posture by category</p>
                                </CardHeader>
                                <CardContent className="py-6">
                                    {categoryEntries.length === 0 ? (
                                        <div className="text-center py-8 text-sm text-muted-foreground">No analytics yet</div>
                                    ) : (
                                        <div className="space-y-3">
                                            {categoryEntries.map(([category, count]) => {
                                                const percentage = totalCategoryCount > 0 ? (count / totalCategoryCount) * 100 : 0;
                                                return (
                                                    <div key={category} className="flex items-center justify-between gap-4">
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                            <Badge variant="outline" className="text-xs font-mono shrink-0">{category}</Badge>
                                                        </div>
                                                        <div className="flex items-center gap-3 flex-shrink-0">
                                                            <div className="w-24 bg-muted rounded-sm h-2 overflow-hidden">
                                                                <div
                                                                    className="bg-primary h-full rounded-sm transition-all"
                                                                    style={{ width: `${percentage}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-sm font-semibold tabular-nums w-12 text-right">{count}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Risk Breakdown */}
                            <Card className="portal-card-elevated">
                                <CardHeader className="border-b pb-4">
                                    <CardTitle className="text-base font-medium">Risk Breakdown</CardTitle>
                                    <p className="text-xs text-muted-foreground mt-1">Actions by risk level</p>
                                </CardHeader>
                                <CardContent className="py-6">
                                    {riskEntries.length === 0 ? (
                                        <div className="text-center py-8 text-sm text-muted-foreground">No analytics yet</div>
                                    ) : (
                                        <div className="space-y-4">
                                            {riskEntries.map(([risk, count]) => {
                                                const totalRisk = riskEntries.reduce((sum, [, c]) => sum + c, 0);
                                                const percentage = totalRisk > 0 ? (count / totalRisk) * 100 : 0;
                                                return (
                                                    <div key={risk} className="space-y-1.5">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-3 h-3 rounded-full ${RISK_COLORS[risk] || "bg-gray-400"}`} />
                                                                <span className={`text-sm font-medium capitalize ${RISK_TEXT_COLORS[risk] || ""}`}>{risk}</span>
                                                            </div>
                                                            <span className="text-sm font-semibold tabular-nums">{count}</span>
                                                        </div>
                                                        <div className="bg-muted rounded-sm h-2 overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-sm transition-all ${RISK_COLORS[risk] || "bg-gray-400"}`}
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>
            )}

            {/* ACTIVITY TAB */}
            {activeTab === "activity" && (
                <GitlabRecentActivity
                    tenantName={tenantName}
                    instanceUrl={instanceUrl}
                    endpointType={endpointType}
                    entityValue={entityValue}
                />
            )}
        </div>
    );
}
